import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ConnectError, createClient } from '@connectrpc/connect';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminService } from 'proto/gen/ts/shirt/v1/admin_pb';
import { ProductService } from 'proto/gen/ts/shirt/v1/product_pb';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TEST_IDS } from '../../utils/testIds';
import { transport } from '../../utils/transport';
const adminClient = createClient(AdminService, transport);
const productClient = createClient(ProductService, transport);
export default function ProductEdit() {
    const { productId } = useParams();
    const navigate = useNavigate();
    const isNew = !productId || productId === 'new';
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [details, setDetails] = useState('');
    const [priceDollars, setPriceDollars] = useState('');
    const [featured, setFeatured] = useState(false);
    const [inStock, setInStock] = useState(true);
    const [leadTimeDays, setLeadTimeDays] = useState(0);
    const [images, setImages] = useState([]);
    const [newImageUrl, setNewImageUrl] = useState('');
    const [defaultDiscountPct, setDefaultDiscountPct] = useState(0);
    const [tiers, setTiers] = useState([]);
    const [savingTiers, setSavingTiers] = useState(false);
    const [tierMessage, setTierMessage] = useState('');
    const [tierError, setTierError] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const queryClient = useQueryClient();
    const { data: productData, isLoading } = useQuery({
        queryKey: [
            'product',
            productId
        ],
        queryFn: ()=>productClient.getProduct({
                productId: productId ?? '',
                slug: ''
            }),
        enabled: !isNew && !!productId
    });
    useEffect(()=>{
        if (productData?.product) {
            const p = productData.product;
            setName(p.name);
            setDescription(p.description);
            setDetails(p.details);
            setPriceDollars((Number(p.priceCents) / 100).toFixed(2));
            setFeatured(p.featured);
            setInStock(p.inStock);
            setLeadTimeDays(p.leadTimeDays);
            setDefaultDiscountPct(p.defaultWholesaleDiscountPct);
            setImages(p.images.map((img)=>({
                    imageUrl: img.imageUrl,
                    altText: img.altText
                })));
            setTiers(p.wholesaleTiers.map((t)=>({
                    minQuantity: t.minQuantity,
                    unitPriceDollars: (Number(t.unitPriceCents) / 100).toFixed(2)
                })));
        }
    }, [
        productData
    ]);
    const handleSave = async ()=>{
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            const priceCents = BigInt(Math.round(Number.parseFloat(priceDollars || '0') * 100));
            const imageInputs = images.map((img, i)=>({
                    $typeName: 'shirt.v1.ProductImageInput',
                    imageUrl: img.imageUrl,
                    altText: img.altText,
                    displayOrder: i
                }));
            const mainImageUrl = imageInputs[0]?.imageUrl ?? '';
            if (isNew) {
                const res = await adminClient.createProduct({
                    name,
                    description,
                    details,
                    priceCents,
                    imageUrl: mainImageUrl,
                    featured,
                    inStock,
                    leadTimeDays,
                    defaultWholesaleDiscountPct: defaultDiscountPct,
                    images: imageInputs
                });
                if (res.product) {
                    setSuccess('Product created');
                    navigate(`/admin/products/${res.product.id}`, {
                        replace: true
                    });
                }
            } else if (productId) {
                await adminClient.updateProduct({
                    productId,
                    name,
                    description,
                    details,
                    priceCents,
                    imageUrl: mainImageUrl,
                    featured,
                    inStock,
                    leadTimeDays,
                    defaultWholesaleDiscountPct: defaultDiscountPct,
                    images: imageInputs
                });
                setSuccess('Product updated');
                queryClient.invalidateQueries({
                    queryKey: [
                        'product',
                        productId
                    ]
                });
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save');
        } finally{
            setSaving(false);
        }
    };
    const addImage = ()=>{
        if (!newImageUrl.trim()) return;
        setImages([
            ...images,
            {
                imageUrl: newImageUrl.trim(),
                altText: ''
            }
        ]);
        setNewImageUrl('');
    };
    const removeImage = (idx)=>{
        setImages(images.filter((_, i)=>i !== idx));
    };
    const addTier = ()=>{
        const nextMin = tiers.length === 0 ? 10 : Math.max(...tiers.map((t)=>t.minQuantity)) + 10;
        setTiers([
            ...tiers,
            {
                minQuantity: nextMin,
                unitPriceDollars: priceDollars
            }
        ]);
    };
    const updateTier = (idx, patch)=>{
        setTiers(tiers.map((t, i)=>i === idx ? {
                ...t,
                ...patch
            } : t));
    };
    const removeTier = (idx)=>{
        setTiers(tiers.filter((_, i)=>i !== idx));
    };
    const handleSaveTiers = async ()=>{
        if (!productId || isNew) {
            setTierError('Save the product first before setting tiers.');
            return;
        }
        setTierError('');
        setTierMessage('');
        setSavingTiers(true);
        try {
            const sorted = [
                ...tiers
            ].sort((a, b)=>a.minQuantity - b.minQuantity);
            await adminClient.setWholesaleTiers({
                productId,
                tiers: sorted.map((t)=>({
                        minQuantity: t.minQuantity,
                        unitPriceCents: BigInt(Math.round(Number.parseFloat(t.unitPriceDollars || '0') * 100))
                    }))
            });
            setTierMessage(`Saved ${sorted.length} tier${sorted.length === 1 ? '' : 's'}`);
            queryClient.invalidateQueries({
                queryKey: [
                    'product',
                    productId
                ]
            });
        } catch (err) {
            setTierError(err instanceof ConnectError ? err.message : 'Failed to save tiers');
        } finally{
            setSavingTiers(false);
        }
    };
    if (isLoading) {
        return /*#__PURE__*/ _jsx("p", {
            className: "text-stone-500 p-8",
            children: "Loading product..."
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex justify-between items-center mb-6",
                children: [
                    /*#__PURE__*/ _jsx("h1", {
                        className: "font-heading text-2xl font-bold text-stone-900",
                        children: isNew ? 'New Product' : 'Edit Product'
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        onClick: ()=>navigate('/admin/products'),
                        className: "text-sm text-stone-500 hover:text-stone-900 underline",
                        children: "Back to Products"
                    })
                ]
            }),
            error && /*#__PURE__*/ _jsx("div", {
                className: "bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm mb-4",
                children: error
            }),
            success && /*#__PURE__*/ _jsx("div", {
                className: "bg-green-50 border border-green-200 text-green-700 px-4 py-3 text-sm mb-4",
                children: success
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "bg-white border-2 border-stone-100 p-6 space-y-5",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                htmlFor: "p-name",
                                className: "block text-xs font-semibold uppercase tracking-wider text-stone-700 mb-1",
                                children: "Name"
                            }),
                            /*#__PURE__*/ _jsx("input", {
                                id: "p-name",
                                "data-testid": TEST_IDS.productFormNameInput,
                                type: "text",
                                value: name,
                                onChange: (e)=>setName(e.target.value),
                                className: "w-full border-2 border-stone-200 px-3 py-2 text-sm focus:border-stone-900 focus:outline-none",
                                required: true
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                htmlFor: "p-desc",
                                className: "block text-xs font-semibold uppercase tracking-wider text-stone-700 mb-1",
                                children: "Description"
                            }),
                            /*#__PURE__*/ _jsx("textarea", {
                                id: "p-desc",
                                "data-testid": TEST_IDS.productFormDescriptionInput,
                                value: description,
                                onChange: (e)=>setDescription(e.target.value),
                                className: "w-full border-2 border-stone-200 px-3 py-2 text-sm focus:border-stone-900 focus:outline-none min-h-[80px]"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                htmlFor: "p-details",
                                className: "block text-xs font-semibold uppercase tracking-wider text-stone-700 mb-1",
                                children: "Details (each line becomes a bullet)"
                            }),
                            /*#__PURE__*/ _jsx("textarea", {
                                id: "p-details",
                                value: details,
                                onChange: (e)=>setDetails(e.target.value),
                                className: "w-full border-2 border-stone-200 px-3 py-2 text-sm focus:border-stone-900 focus:outline-none min-h-[80px]",
                                placeholder: 'Solid aluminum body\nMagnetic closure\n4-piece'
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "grid grid-cols-1 sm:grid-cols-3 gap-4",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("label", {
                                        htmlFor: "p-price",
                                        className: "block text-xs font-semibold uppercase tracking-wider text-stone-700 mb-1",
                                        children: "Price ($)"
                                    }),
                                    /*#__PURE__*/ _jsx("input", {
                                        id: "p-price",
                                        "data-testid": TEST_IDS.productFormPriceInput,
                                        type: "number",
                                        step: "0.01",
                                        min: "0",
                                        value: priceDollars,
                                        onChange: (e)=>setPriceDollars(e.target.value),
                                        className: "w-full border-2 border-stone-200 px-3 py-2 text-sm focus:border-stone-900 focus:outline-none"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("label", {
                                        htmlFor: "p-lead",
                                        className: "block text-xs font-semibold uppercase tracking-wider text-stone-700 mb-1",
                                        children: "Lead time (days)"
                                    }),
                                    /*#__PURE__*/ _jsx("input", {
                                        id: "p-lead",
                                        type: "number",
                                        min: "0",
                                        value: leadTimeDays,
                                        onChange: (e)=>setLeadTimeDays(Number.parseInt(e.target.value || '0', 10)),
                                        className: "w-full border-2 border-stone-200 px-3 py-2 text-sm focus:border-stone-900 focus:outline-none"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-end gap-4 pb-1",
                                children: [
                                    /*#__PURE__*/ _jsxs("label", {
                                        className: "flex items-center gap-2 cursor-pointer",
                                        children: [
                                            /*#__PURE__*/ _jsx("input", {
                                                type: "checkbox",
                                                checked: featured,
                                                onChange: (e)=>setFeatured(e.target.checked)
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-xs font-semibold uppercase tracking-wider text-stone-700",
                                                children: "Featured"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("label", {
                                        className: "flex items-center gap-2 cursor-pointer",
                                        children: [
                                            /*#__PURE__*/ _jsx("input", {
                                                type: "checkbox",
                                                checked: inStock,
                                                onChange: (e)=>setInStock(e.target.checked)
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-xs font-semibold uppercase tracking-wider text-stone-700",
                                                children: "In stock"
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "block text-xs font-semibold uppercase tracking-wider text-stone-700 mb-1",
                                children: "Images"
                            }),
                            images.length === 0 ? /*#__PURE__*/ _jsx("p", {
                                className: "text-xs text-stone-500 mb-2",
                                children: "No images yet. Add URLs below."
                            }) : /*#__PURE__*/ _jsx("div", {
                                className: "grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3",
                                children: images.map((img, i)=>/*#__PURE__*/ _jsxs("div", {
                                        className: "relative border-2 border-stone-100 overflow-hidden group",
                                        children: [
                                            /*#__PURE__*/ _jsx("img", {
                                                src: img.imageUrl,
                                                alt: img.altText || `Image ${i + 1}`,
                                                className: "w-full aspect-square object-cover"
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "absolute bottom-0 left-0 right-0 bg-white/90 px-2 py-1 text-[10px] text-stone-600",
                                                children: i === 0 ? 'Primary' : `#${i + 1}`
                                            }),
                                            /*#__PURE__*/ _jsx("button", {
                                                type: "button",
                                                onClick: ()=>removeImage(i),
                                                className: "absolute top-1 right-1 w-6 h-6 bg-red-600 text-white flex items-center justify-center text-xs opacity-0 group-hover:opacity-100",
                                                children: "×"
                                            })
                                        ]
                                    }, `${img.imageUrl}-${i}`))
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx("input", {
                                        type: "url",
                                        value: newImageUrl,
                                        onChange: (e)=>setNewImageUrl(e.target.value),
                                        placeholder: "https://...",
                                        className: "flex-1 border-2 border-stone-200 px-3 py-2 text-sm focus:border-stone-900 focus:outline-none"
                                    }),
                                    /*#__PURE__*/ _jsx("button", {
                                        type: "button",
                                        onClick: addImage,
                                        className: "px-4 py-2 bg-stone-200 text-stone-900 text-xs font-semibold uppercase tracking-wider hover:bg-stone-300",
                                        children: "Add"
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "pt-4 border-t border-stone-100",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "block text-xs font-semibold uppercase tracking-wider text-stone-700 mb-2",
                                children: "Wholesale pricing"
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-3 mb-4",
                                children: [
                                    /*#__PURE__*/ _jsx("label", {
                                        htmlFor: "p-default-discount",
                                        className: "text-xs text-stone-700",
                                        children: "Default discount when no tier matches"
                                    }),
                                    /*#__PURE__*/ _jsx("input", {
                                        id: "p-default-discount",
                                        "data-testid": TEST_IDS.productFormDefaultDiscountInput,
                                        type: "number",
                                        min: "0",
                                        max: "100",
                                        value: defaultDiscountPct,
                                        onChange: (e)=>setDefaultDiscountPct(Number.parseInt(e.target.value || '0', 10)),
                                        className: "w-20 border-2 border-stone-200 px-2 py-1 text-sm focus:border-stone-900 focus:outline-none"
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-xs text-stone-500",
                                        children: "%"
                                    })
                                ]
                            }),
                            tiers.length === 0 ? /*#__PURE__*/ _jsx("p", {
                                className: "text-xs text-stone-500 mb-3",
                                children: "No quantity tiers. Add one to set a unit price that kicks in at a threshold."
                            }) : /*#__PURE__*/ _jsx("div", {
                                className: "mb-3 space-y-2",
                                children: [
                                    ...tiers
                                ].sort((a, b)=>a.minQuantity - b.minQuantity).map((t, i)=>{
                                    const origIdx = tiers.findIndex((x)=>x.minQuantity === t.minQuantity && x.unitPriceDollars === t.unitPriceDollars);
                                    return /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center gap-3",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-xs text-stone-500",
                                                children: "At"
                                            }),
                                            /*#__PURE__*/ _jsx("input", {
                                                type: "number",
                                                min: "1",
                                                "aria-label": "Minimum quantity",
                                                value: t.minQuantity,
                                                onChange: (e)=>updateTier(origIdx, {
                                                        minQuantity: Math.max(1, Number.parseInt(e.target.value || '1', 10))
                                                    }),
                                                className: "w-20 border-2 border-stone-200 px-2 py-1 text-sm focus:border-stone-900 focus:outline-none"
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-xs text-stone-500",
                                                children: "units →"
                                            }),
                                            /*#__PURE__*/ _jsx("input", {
                                                type: "number",
                                                step: "0.01",
                                                min: "0",
                                                "aria-label": "Unit price in dollars",
                                                value: t.unitPriceDollars,
                                                onChange: (e)=>updateTier(origIdx, {
                                                        unitPriceDollars: e.target.value
                                                    }),
                                                className: "w-24 border-2 border-stone-200 px-2 py-1 text-sm focus:border-stone-900 focus:outline-none"
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-xs text-stone-500",
                                                children: "$ / unit"
                                            }),
                                            /*#__PURE__*/ _jsx("button", {
                                                type: "button",
                                                onClick: ()=>removeTier(origIdx),
                                                className: "ml-auto text-xs text-red-600 hover:text-red-800",
                                                children: "Remove"
                                            })
                                        ]
                                    }, `${t.minQuantity}-${i}`);
                                })
                            }),
                            tierError && /*#__PURE__*/ _jsx("div", {
                                className: "bg-red-50 border border-red-200 text-red-700 px-3 py-2 text-xs mb-2",
                                children: tierError
                            }),
                            tierMessage && /*#__PURE__*/ _jsx("div", {
                                className: "bg-green-50 border border-green-200 text-green-700 px-3 py-2 text-xs mb-2",
                                children: tierMessage
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx("button", {
                                        type: "button",
                                        "data-testid": TEST_IDS.productFormAddTierButton,
                                        onClick: addTier,
                                        className: "px-3 py-2 bg-stone-200 text-stone-900 text-xs font-semibold uppercase tracking-wider hover:bg-stone-300",
                                        children: "Add tier"
                                    }),
                                    /*#__PURE__*/ _jsx("button", {
                                        type: "button",
                                        "data-testid": TEST_IDS.productFormSaveTiersButton,
                                        onClick: handleSaveTiers,
                                        disabled: savingTiers || isNew,
                                        title: isNew ? 'Save the product first, then add tiers.' : undefined,
                                        className: "px-3 py-2 bg-stone-900 text-white text-xs font-semibold uppercase tracking-wider hover:bg-stone-700 disabled:opacity-60",
                                        children: savingTiers ? 'Saving tiers...' : 'Save tiers'
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-3 pt-4 border-t border-stone-100",
                        children: [
                            /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                "data-testid": TEST_IDS.productFormSaveButton,
                                onClick: handleSave,
                                disabled: saving,
                                className: "px-6 py-3 bg-stone-900 text-white text-xs font-semibold uppercase tracking-wider hover:bg-stone-700 disabled:opacity-60",
                                children: saving ? 'Saving...' : 'Save'
                            }),
                            /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                onClick: ()=>navigate('/admin/products'),
                                className: "px-6 py-3 border-2 border-stone-200 text-stone-700 text-xs font-semibold uppercase tracking-wider hover:border-stone-900",
                                children: "Cancel"
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
